import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const navItems: { label: string; href: string; external?: boolean; page?: boolean }[] = [
  { label: "Home", href: "/" },
  { label: "Framework", href: "#framework" },
  { label: "Learn", href: "https://learn.cashlearn.org", external: true },
  { label: "Research", href: "#research" },
  { label: "Founder", href: "/founder", page: true },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isFounderPage = location.pathname === "/founder";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
      const sections = navItems.map((item) => item.href.replace("#", ""));
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i]);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 100) { setActiveSection(sections[i]); break; }
        }
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string, external?: boolean, page?: boolean) => {
    if (external) { setMenuOpen(false); return; }
    e.preventDefault();
    if (page) { navigate(href); window.scrollTo({ top: 0, behavior: "smooth" }); setMenuOpen(false); return; }
    if (href === "/") { navigate("/"); setMenuOpen(false); return; }
    const id = href.replace("#", "");
    if (isFounderPage) {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top, behavior: "smooth" });
        }
      }, 350);
      setMenuOpen(false);
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: "smooth" });
    }
    setMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 overflow-visible ${
        scrolled || isFounderPage ? "bg-white/95 backdrop-blur-md border-b border-[#dbeafe]" : "bg-transparent"
      }`}
    >
      <nav className="flex items-center justify-between px-6 md:px-12 h-[72px]">
        <a href="#home" onClick={(e) => handleNavClick(e, "#home")} className="flex items-center cursor-pointer overflow-visible">
          <img
            src="https://static.readdy.ai/image/a3e8b0182dc4ba81eda0b98b43a24071/e6cb8480525ab3bf840077cea618bf7c.png"
            alt="CashLearn Logo"
            style={{ height: "160px", width: "auto", marginTop: "0px" }}
            className={`object-contain transition-all duration-500 flex-shrink-0 ${scrolled || isFounderPage ? "brightness-0" : "brightness-0 invert"}`}
          />
        </a>

        <ul className="hidden md:flex items-center gap-1">
          {navItems.map((item) => {
            const id = item.href.replace("#", "");
            const isActive = activeSection === id;
            return (
              <li key={item.label}>
                <a
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href, item.external, item.page)}
                  target={item.external ? "_blank" : undefined}
                  rel={item.external ? "noopener noreferrer" : undefined}
                  className={`relative px-3 py-2 text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap rounded-md ${
                    isActive
                      ? "text-[#1d4ed8]"
                      : scrolled || isFounderPage
                      ? "text-[#1e3a5f] hover:text-[#1d4ed8]"
                      : "text-white/90 hover:text-white"
                  }`}
                >
                  {item.label}
                  {isActive && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#3b82f6] rounded-full" />}
                </a>
              </li>
            );
          })}
        </ul>

        <button
          className={`md:hidden w-9 h-9 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
            scrolled || isFounderPage ? "text-[#1e3a5f]" : "text-white"
          }`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`ri-${menuOpen ? "close" : "menu"}-line text-xl`} />
        </button>
      </nav>

      {menuOpen && (
        <div className="md:hidden bg-white border-t border-[#dbeafe] px-6 py-4 flex flex-col gap-1">
          {navItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={(e) => handleNavClick(e, item.href, item.external, item.page)}
              target={item.external ? "_blank" : undefined}
              rel={item.external ? "noopener noreferrer" : undefined}
              className="py-2.5 px-3 text-sm font-medium text-[#1e3a5f] hover:text-[#1d4ed8] hover:bg-[#eff6ff] rounded-md transition-colors cursor-pointer"
            >
              {item.label}
            </a>
          ))}
        </div>
      )}
    </header>
  );
}
